import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import {
  ScrollView,
  View,
  Text,
  TouchableHighlight,
  TouchableOpacity,
  TextInput,
  Pressable,
  Platform,
} from 'react-native';
import { Picker } from '@react-native-picker/picker';
import { ActivityIndicator } from 'react-native-paper';
import Checkbox from 'expo-checkbox';
import { FontAwesome5 } from '@expo/vector-icons';
import * as XLSX from 'xlsx-js-style';

import {
  getProductsData,
  deleteProduct,
  getProductCategoriesData,
  getProductFamiliesData,
  getProductLinesData,
  getProductDetailByBarcode,
  getProductFields,
  getProductColumnConfig,
} from '../../../../api/Product';
import { getLocationsData } from '../../../../api/Settings';
import { msgStr } from '../../../../common/constants/Message';
import { TextMediumSize } from '../../../../common/constants/Fonts';
import { useAlertModal, useConfirmModal } from '../../../../common/hooks';
import BasicLayout from '../../../../common/components/CustomLayout/BasicLayout';
import { renderBOHTlbDatePicker } from '../../../../common/components/bohtoolbar';
import { formatDate, formatDateTime } from '../../../../common/utils/DateUtils';

import { productsStyle } from './styles/ProductsStyle';
import { setQuickScanReturnSubItem } from '../../inventoryNavigationState';
import AddProductModal from './AddProductModal';
import QuickAddProductModal from './QuickAddProductModal';
import BulkUpdateLocationModal from './BulkUpdateLocationModal';
import BulkUpdateStatus from './BulkUpdateStatus';
import UpdateLocationModal from '../locationhistory/UpdateLocationModal';
import ProductQRModal from './ProductQRModal';
import ColumnPickerModal, { buildColumns } from './ColumnPickerModal';
import ImportProductsModal from './ImportProductsModal';

const Products = ({ navigation, openInventory, data }) => {
  const initialMount = useRef(true);
  const StatusObj = {
    0: 'Ready',
    1: 'Ordered',
    2: 'Checked out',
    3: 'Checked in',
    4: 'Broken',
    5: 'Sold',
    6: 'Transferred',
    7: 'Stolen',
    8: 'Trashed',
  };

  const { showAlert } = useAlertModal();
  const { showConfirm } = useConfirmModal();

  const [tableData, setTableData] = useState([]);
  const [updateProductTrigger, setUpdateProductsTrigger] = useState(false);
  const [checkedItemIds, setCheckedItemIds] = useState([]);
  const [isCheckedAll, setIsCheckAll] = useState(false);

  const [isAddModalVisible, setAddModalVisible] = useState(false);
  const [isQuickAddModalVisible, setQuickAddModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [isBulkUpdateLocationVisible, setBulkUpdateLocationVisible] = useState(false);
  const [isBulkUpdateStatusVisible, setBulkUpdateStatusVisible] = useState(false);
  const [isUpdateLocationVisible, setUpdateLocationVisible] = useState(false);

  const [isQRModalVisible, setQRModalVisible] = useState(false);
  const [selectedQRProduct, setSelectedQRProduct] = useState(null);

  const openQRModal = (product) => {
    setSelectedQRProduct(product);
    setQRModalVisible(true);
  };
  const closeQRModal = () => {
    setQRModalVisible(false);
    setSelectedQRProduct(null);
  };

  const openAddProductModal = () => {
    setAddModalVisible(true);
    setSelectedProduct(null);
  };
  const closeAddProductModal = () => {
    setAddModalVisible(false);
    setSelectedProduct(null);
  };
  const editProduct = (item) => {
    setSelectedProduct(item);
    setAddModalVisible(true);
  };

  // From Add modal: barcode already exists — switch into update mode for that item
  const viewExistingProductFromAdd = (item) => {
    setAddModalVisible(false);
    setSelectedProduct(null);
    setTimeout(() => {
      setSelectedProduct(item);
      setAddModalVisible(true);
    }, 50);
  };

  const closeUpdateLocationModal = () => { 
    setUpdateLocationVisible(false); 
    setSelectedProduct(null);
  }

  const handleUpdateLocationModal = (item) => {
    setSelectedProduct(item);
    setUpdateLocationVisible(true);
  };

  const openQuickAddProductModal = () => {
    setQuickAddModalVisible(true);
    setSelectedProduct(null);
  };
  const closeQuickAddProductModal = () => {
    setQuickAddModalVisible(false);
    setSelectedProduct(null);
  };

  const bulkUpdateLocation = () => { setBulkUpdateLocationVisible(true); }
  const closeBulkUpdateLocation = () => { setBulkUpdateLocationVisible(false); }

  const blukUPdateStatus = () => { setBulkUpdateStatusVisible(true); }
  const closeBlukUPdateStatus = () => { setBulkUpdateStatusVisible(false); }

  const [searchCategory, setSearchCategory] = useState(0);
  const [searchFamily, setSearchFamily] = useState(0);
  const [searchProductLine, setSearchProductLine] = useState(0);
  const [searchBarcode, setSearchBarcode] = useState('');
  const [searchSize, setSearchSize] = useState('');
  const [searchHomeLocation, setSearchHomeLocation] = useState(0);
  const [searchCurrentLocation, setSearchCurrentLocation] = useState(0);
  const [searchStatus, setSearchStatus] = useState<number | ''>('');
  const [searchLastScannedStart, setSearchLastScannedStart] = useState('');
  const [searchLastScannedEnd, setSearchLastScannedEnd] = useState('');

  // Inline quick-scan: scan/type a barcode right on the list page to jump
  // straight to that item's history, without leaving for the Quick Scan
  // (add new item) screen.
  const [quickScanBarcode, setQuickScanBarcode] = useState('');
  const [quickScanLoading, setQuickScanLoading] = useState(false);
  const quickScanInputRef = useRef<any>(null);

  const [categories, setCategories] = useState([]);
  const [families, setFamilies] = useState([]);
  const [lines, setLines] = useState([]);
  const [Locations, setLocations] = useState([]);

  // Custom inventory fields (Settings -> Inventory Fields) and the
  // tenant-wide column visibility config.
  const [productFields, setProductFields] = useState<any[]>([]);
  const [visibleColumnIds, setVisibleColumnIds] = useState<string[] | null>(null);
  const [isColumnPickerVisible, setColumnPickerVisible] = useState(false);
  const [isImportModalVisible, setImportModalVisible] = useState(false);

  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [isLoading, setIsLoading] = useState(true);

  const allColumns = useMemo(() => buildColumns(productFields), [productFields]);
  const fieldById = useMemo(() => {
    const m: Record<number, any> = {};
    (productFields || []).forEach(f => { m[f.id] = f; });
    return m;
  }, [productFields]);
  const visibleColumns = useMemo(
    () => allColumns.filter(c => !visibleColumnIds || visibleColumnIds.includes(c.id)),
    [allColumns, visibleColumnIds]
  );

  // Plain-text value used for sorting and export.
  const getCellText = (item: any, colId: string): string => {
    switch (colId) {
      case 'category': return item.category ? item.category.category : '';
      case 'family': return item.family ? item.family.family : '';
      case 'line': return item.line ? item.line.line + (item.item_starting_number ? ' - ' + item.item_starting_number : '') : '';
      case 'size': return item.line ? item.line.size : '';
      case 'barcode': return item.barcode || '';
      case 'license_number': return item.license_number || '';
      case 'home_location': return item.home_location_tbl ? getLocationLabel(item.home_location_tbl) : '';
      case 'current_location': return item.current_location_tbl ? getLocationLabel(item.current_location_tbl) : '';
      case 'status': return item.status != null ? StatusObj[item.status] : '';
      case 'tracking_tag': return item.tracking_tag || '';
      case 'last_scanned_at': return item.last_scanned_at ? formatDateTime(new Date(item.last_scanned_at)) : '';
      default: {
        if (colId.startsWith('meta_')) {
          const fid = Number(colId.slice(5));
          const v = item.meta_values?.[fid];
          return v !== undefined && v !== null ? String(v) : (fieldById[fid]?.default_value || '');
        }
        return '';
      }
    }
  };

  const renderCellContent = (item: any, colId: string) => {
    if (colId === 'home_location' || colId === 'current_location') {
      const loc = colId === 'home_location' ? item.home_location_tbl : item.current_location_tbl;
      return (
        <>
          <Text>{loc ? getLocationLabel(loc) : ''}</Text>
          {loc?.brands && (
            <View style={{ marginTop: 4 }}>
              <Text style={{ fontSize: 10, fontWeight: '600', color: '#666' }}>Linked Brands:</Text>
              {loc.brands.split(', ').map((brand, idx) => (
                <Text key={idx} style={{ fontSize: 10, color: '#dc2626', marginTop: 2 }}>
                  {brand}
                </Text>
              ))}
            </View>
          )}
        </>
      );
    }
    return <Text>{getCellText(item, colId)}</Text>;
  };

  const getSortedData = (tableData) => {
    if(sortColumn && sortDirection){
      tableData.sort((a, b) => {
        let A = getCellText(a, sortColumn);
        let B = getCellText(b, sortColumn);

        if (typeof A === 'number' && typeof B === 'number') {
          return sortDirection === 'asc' ? A - B : B - A;
        } else {
          const textA = String(A).toUpperCase();
          const textB = String(B).toUpperCase();
          return sortDirection === 'asc' ? textA.localeCompare(textB) : textB.localeCompare(textA);
        }
      });

      return tableData;
    }else return tableData;
  }

  const filteredAndSortedData = useMemo(()=>{
    const filteredData = tableData.filter((item) => {
      const isBarcodeMatch = searchBarcode.trim()
        ? item.barcode && item.barcode.toLowerCase().includes(searchBarcode.trim().toLowerCase())
        : true;
      const isSizeMatch = searchSize.trim()
        ? item.line && item.line.size.toLowerCase().includes(searchSize.trim().toLowerCase())
        : true;
      const isHomeLocationMatch =
        searchHomeLocation != 0
          ? (item.home_location && item.home_location == searchHomeLocation)
          : true;
      const isCurrentLocationMatch =
        searchCurrentLocation != 0
          ? (item.current_location && item.current_location == searchCurrentLocation)
          : true;
      const isStatusMatch = searchStatus !== '' ? item.status == searchStatus : true;

      const scannedAt = item.last_scanned_at ? new Date(item.last_scanned_at) : null;
      const isLastScannedStartMatch = searchLastScannedStart
        ? (scannedAt && scannedAt >= new Date(`${searchLastScannedStart} 00:00:00`))
        : true;
      const isLastScannedEndMatch = searchLastScannedEnd
        ? (scannedAt && scannedAt <= new Date(`${searchLastScannedEnd} 23:59:59`))
        : true;

      return isBarcodeMatch && isSizeMatch && isHomeLocationMatch && isCurrentLocationMatch && isStatusMatch
        && isLastScannedStartMatch && isLastScannedEndMatch;
    });

    return getSortedData(filteredData);

  }, [tableData, searchBarcode, searchSize, searchHomeLocation, searchCurrentLocation, searchStatus, searchLastScannedStart, searchLastScannedEnd, sortColumn, sortDirection]);

  useEffect(() => {
    if (data == null && updateProductTrigger == true) {
      getTable();
    }
  }, [updateProductTrigger]);

  useEffect(() => {
    if (initialMount.current == false) {
      let timeoutId = setTimeout(() => {
        getTable();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [searchCategory, searchFamily, searchProductLine]);

  useEffect(() => {
    if (initialMount.current == false) {
      if (searchCategory != 0) {
        loadProductFamiliesData(searchCategory, (jsonRes) => {
          setFamilies(jsonRes);
          if (jsonRes.length > 0) setSearchFamily(jsonRes[0].id);
          else {
            setSearchFamily(0);
          }
        });
      } else {
        setFamilies([]);
        setSearchFamily(0);
      }
    }
  }, [searchCategory]);

  useEffect(() => {
    if (initialMount.current == false) {
      if (searchFamily != 0) {
        loadProductLinesData(searchFamily, (jsonRes) => {
          setLines(jsonRes);
          if (jsonRes.length > 0) setSearchProductLine(jsonRes[0].id);
          else {
            setSearchProductLine(0);
          }
        });
      } else {
        setLines([]);
        setSearchProductLine(0);
      }
    }
  }, [searchFamily]);

  useEffect(() => {
    if (data)
      if (data.searchOptions) {
        setTimeout(() => {
          if (data.searchOptions.categoryId) setSearchCategory(data.searchOptions.categoryId);
          setTimeout(() => {
            if (data.searchOptions.familyId) setSearchFamily(data.searchOptions.familyId);
            setTimeout(() => {
              if (data.searchOptions.lineId) setSearchProductLine(data.searchOptions.lineId);
              data = null;
            }, 50);
          }, 50);
        }, 50);
      }
  }, [data]);

  useEffect(() => {
    loadProductCategoriesData((categories) => {
      const categoryId = categories[0] ? categories[0].id : null;
      loadProductFamiliesData(categoryId, (families) => {
        const familyId = families[0] ? families[0].id : null;
        loadProductLinesData(familyId, (lines) => {
          if (categories.length) {
            setCategories(categories);
            // setSearchCategory(categories[0].id);
          } else setCategories([]);

          if (families.length) {
            setFamilies(families);
            // setSearchFamily(families[0].id);
          } else setFamilies([]);

          if (lines.length) {
            setLines(lines);
            // setSearchProductLine(lines[0].id);
          } else setLines([]);

          setUpdateProductsTrigger(true);
          initialMount.current = false;
        });
      });
    });
    getLocationsData({}, (jsonRes, status, error) => {
      console.log('📍 Locations API Response:', { status, count: jsonRes?.length, data: jsonRes });
      if (status == 200) setLocations(jsonRes);
    });
    loadProductFields();
    getProductColumnConfig((jsonRes, status) => {
      if (status === 200 && Array.isArray(jsonRes?.columns)) {
        setVisibleColumnIds(jsonRes.columns);
      }
    });
  }, []);

  const loadProductFields = () => {
    getProductFields((jsonRes, status) => {
      if (status === 200 && Array.isArray(jsonRes)) setProductFields(jsonRes);
    });
  };

  const getLocationLabel = (loc?: {name?: string; location?: string;}) => {
    return `${loc?.name ? loc.name + ' - ' : ''}${loc?.location ?? 'No address available'}`;
  }

  const loadProductCategoriesData = (callback) => {
    getProductCategoriesData((jsonRes, status, error) => {
      switch (status) {
        case 200:
          callback(jsonRes);
          break;
        case 500:
          showAlert('error', msgStr('serverError'));
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });
  };

  const loadProductFamiliesData = (categoryId, callback) => {
    getProductFamiliesData(categoryId, (jsonRes, status, error) => {
      switch (status) {
        case 200:
          callback(jsonRes);
          break;
        case 500:
          showAlert('error', msgStr('serverError'));
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });
  };

  const loadProductLinesData = (familyId, callback) => {
    getProductLinesData(familyId, (jsonRes, status, error) => {
      switch (status) {
        case 200:
          callback(jsonRes);
          break;
        case 500:
          showAlert('error', msgStr('serverError'));
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });
  };

  const removeProduct = (id) => {
    showConfirm(msgStr('deleteConfirmStr'), () => {
      deleteProduct(id, (jsonRes, status, error) => {
        switch (status) {
          case 200:
            setUpdateProductsTrigger(true);
            showAlert('success', jsonRes.message);
            break;
          default:
            if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
            else showAlert('error', msgStr('unknownError'));
            break;
        }
      });
    });
  };

  const getTable = () => {
    const CFLOption = {
      category_id: searchCategory,
      family_id: searchFamily,
      line_id: searchProductLine,
    };

    setIsLoading(true);
    getProductsData(CFLOption, (jsonRes, status, error) => {
      switch (status) {
        case 200:
          setUpdateProductsTrigger(false);
          setTableData(jsonRes);
          break;
        case 500:
          showAlert('error', msgStr('serverError'));
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });
  };

  // Inline quick-scan on the list page: scan/type a barcode and jump straight
  // to that item's scan/reservation history, without navigating to the
  // separate "Quick Scan" (add new item) screen. Looking up the product this
  // way also stamps last_scanned_at server-side.
  const handleQuickScan = () => {
    const cleanBarcode = quickScanBarcode.trim();
    if (!cleanBarcode || quickScanLoading) return;

    setQuickScanLoading(true);
    getProductDetailByBarcode({ barcode: cleanBarcode }, (jsonRes, status) => {
      setQuickScanLoading(false);
      switch (status) {
        case 200:
          setQuickScanBarcode('');
          navigation.navigate('ProductHistory', { product: jsonRes });
          setUpdateProductsTrigger(true);
          setTimeout(() => quickScanInputRef.current?.focus(), 100);
          break;
        case 404:
          showAlert('error', 'No product found for that barcode');
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });
  };

  // Export the currently filtered/sorted list to a spreadsheet (.xlsx).
  // Web-only, matching the pattern used by other reports (e.g. Order Potential).
  const exportToSpreadsheet = () => {
    if (Platform.OS !== 'web') {
      showAlert('info', 'Export is only available on web platform');
      return;
    }

    if (!filteredAndSortedData || filteredAndSortedData.length === 0) {
      showAlert('error', 'No data available to export');
      return;
    }

    try {
      const headers = visibleColumns.map(c => c.label + (c.sub ? ` ${c.sub}` : ''));

      const rows = filteredAndSortedData.map((item: any) =>
        visibleColumns.map(c => getCellText(item, c.id))
      );

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

      ws['!cols'] = headers.map(() => ({ wch: 20 }));

      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_col(C) + "1";
        if (!ws[address]) continue;
        ws[address].s = {
          font: { bold: true },
          alignment: { horizontal: "center" }
        };
      }

      XLSX.utils.book_append_sheet(wb, ws, "Inventory");

      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `inventory_products_${timestamp}.xlsx`;

      XLSX.writeFile(wb, filename);
      showAlert('success', `Exported ${filteredAndSortedData.length} records to ${filename}`);
    } catch (error) {
      console.error('Export error:', error);
      showAlert('error', 'Failed to export data');
    }
  };

  const setIsChecked = (itemId, value) => {
    setIsLoading(true);
    setCheckedItemIds((prevIds) => {
      if (value) {
        return [...prevIds, itemId];
      } else {
        return prevIds.filter((id) => id !== itemId);
      }
    });
  }

  const blukDelete = useCallback(() => {
    if(!checkedItemIds || !Array.isArray(checkedItemIds) || checkedItemIds.length == 0){
      showAlert('warning', 'Please select products');
      return;
    }
  
    deleteProduct(checkedItemIds, (jsonRes) => {
      showAlert('success', jsonRes.message);
      setUpdateProductsTrigger(true);
    });
  }, [checkedItemIds]);

  const updateCheckedAll = (value) => {
    setIsLoading(true);
    const updatedCheckedItemIds = value ? filteredAndSortedData.map((item) => item.id) : [];
    setCheckedItemIds(updatedCheckedItemIds);
    setIsCheckAll(value);
  }

  const sortData = (column) =>{
    setIsLoading(true);
    const direction = column === sortColumn && sortDirection === 'desc' ? 'asc' : 'desc';

    setSortColumn(column);
    setSortDirection(direction);
  }

  const renderTableData = () => {
    const rows = [];
    if (filteredAndSortedData.length > 0) {
      filteredAndSortedData.map((item: any, index: number) => {
        rows.push(
          <View key={index} style={styles.tableRow}>
            <View style={[styles.cell, {width:50, alignItems:"center", justifyContent:'center', padding:0}]}>
              <Checkbox 
                value={checkedItemIds.includes(item.id)}
                onValueChange={(value)=>setIsChecked(item.id, value)}/>
            </View>
            {visibleColumns.map(col => (
              <View key={col.id} style={[styles.cell, col.width ? { width: col.width } : null]}>
                {renderCellContent(item, col.id)}
              </View>
            ))}
            <View style={[styles.IconCell]}>
              <TouchableOpacity
                onPress={() => {
                  if (item.barcode) openQRModal(item);
                  else showAlert('error', 'No barcode linked with this product');
                }}
              >
                <FontAwesome5 size={TextMediumSize} name="qrcode" color="black" />
              </TouchableOpacity>
            </View>
            <View style={[styles.IconCell]}>
              <TouchableOpacity
                onPress={() => {
                  navigation.navigate('ProductHistory', { product: item });
                }}
              >
                <FontAwesome5 size={TextMediumSize} name="history" color="black" />
              </TouchableOpacity>
            </View>
            <View style={[styles.IconCell]}>
              <TouchableOpacity
                onPress={() => {
                  handleUpdateLocationModal(item);
                }}
              >
                <FontAwesome5 size={TextMediumSize} name="sync" color="black" />
              </TouchableOpacity>
            </View>
            <View style={[styles.IconCell]}>
              <TouchableOpacity
                onPress={() => {
                  editProduct(item);
                }}
              >
                <FontAwesome5 size={TextMediumSize} name="edit" color="black" />
              </TouchableOpacity>
            </View>
            <View style={[styles.IconCell]}>
              <TouchableOpacity
                onPress={() => {
                  removeProduct(item.id);
                }}
              >
                <FontAwesome5 size={TextMediumSize} name="times" color="black" />
              </TouchableOpacity>
            </View>
          </View>
        );
      });
    } else {
      <></>;
    }
    setIsLoading(false);
    return <>{rows}</>;
  };

  const buttonsElement = useMemo(()=>(<View style={styles.toolbar}>
    <TouchableHighlight style={styles.button} onPress={openAddProductModal}>
      <Text style={styles.buttonText}>Add</Text>
    </TouchableHighlight>
    <TouchableHighlight style={styles.button} onPress={openQuickAddProductModal}>
      <Text style={styles.buttonText}>Quick Add</Text>
    </TouchableHighlight>
    <TouchableHighlight
      style={styles.quickScanButton}
      onPress={() => {
        setQuickScanReturnSubItem('Products');
        navigation.navigate('InventoryQuickScan');
      }}
    >
      <View style={{flexDirection: 'row', alignItems: 'center'}}>
        <FontAwesome5 name="barcode" size={12} color="white" style={{marginRight: 8}} />
        <Text style={styles.buttonText}>Quick Scan</Text>
      </View>
    </TouchableHighlight>
    <View style={{flexDirection: 'row', alignItems: 'center', marginLeft: 20}}>
      <TextInput
        ref={quickScanInputRef}
        style={[styles.searchInput, {width: 180, backgroundColor: 'white'}]}
        placeholder="Scan barcode for history..."
        value={quickScanBarcode}
        onChangeText={setQuickScanBarcode}
        onSubmitEditing={handleQuickScan}
      />
      <TouchableHighlight style={styles.button} onPress={handleQuickScan} disabled={quickScanLoading}>
        <View style={{flexDirection: 'row', alignItems: 'center'}}>
          <FontAwesome5 name="history" size={12} color="white" style={{marginRight: 8}} />
          <Text style={styles.buttonText}>{quickScanLoading ? 'Looking up...' : 'Scan & View History'}</Text>
        </View>
      </TouchableHighlight>
    </View>
    <TouchableHighlight style={styles.button} onPress={() => setImportModalVisible(true)}>
      <View style={{flexDirection: 'row', alignItems: 'center'}}>
        <FontAwesome5 name="file-import" size={12} color="white" style={{marginRight: 8}} />
        <Text style={styles.buttonText}>Import</Text>
      </View>
    </TouchableHighlight>
    <TouchableHighlight style={styles.button} onPress={exportToSpreadsheet}>
      <View style={{flexDirection: 'row', alignItems: 'center'}}>
        <FontAwesome5 name="file-export" size={12} color="white" style={{marginRight: 8}} />
        <Text style={styles.buttonText}>Export</Text>
      </View>
    </TouchableHighlight>
    <TouchableHighlight style={styles.button} onPress={() => setColumnPickerVisible(true)}>
      <View style={{flexDirection: 'row', alignItems: 'center'}}>
        <FontAwesome5 name="columns" size={12} color="white" style={{marginRight: 8}} />
        <Text style={styles.buttonText}>Columns</Text>
      </View>
    </TouchableHighlight>
    <Text style={{marginLeft:50, marginRight:5}}>Bulk Actions</Text>
    <TouchableHighlight style={styles.bulkbutton} onPress={bulkUpdateLocation}>
      <Text style={styles.buttonText}>Update Location</Text>
    </TouchableHighlight>
    <TouchableHighlight style={styles.bulkbutton} onPress={blukUPdateStatus}>
      <Text style={styles.buttonText}>Update Status</Text>
    </TouchableHighlight>
    <TouchableHighlight style={styles.bulkbutton} onPress={blukDelete}>
      <Text style={styles.buttonText}>Delete</Text>
    </TouchableHighlight>
  </View>), [checkedItemIds, quickScanBarcode, quickScanLoading, filteredAndSortedData, visibleColumns]);

  const tableElement = useMemo(()=>(<View style={styles.tableContainer}>
    <View style={styles.tableHeader}>
      <View style={[styles.columnHeader, {width:50, alignItems:"center", justifyContent:'center', padding:0}]}>
        <Checkbox value={isCheckedAll} onValueChange={updateCheckedAll}/>
      </View>
      {visibleColumns.map(col => (
        <Pressable
          key={col.id}
          style={[styles.columnHeader, styles.sortableColumn, col.width ? { width: col.width } : null]}
          onPress={() => sortData(col.id)}
        >
          {col.sub ? (
            <View>
              <Text>{col.label}</Text>
              <Text style={{ fontSize: 10, color: '#dc2626', fontWeight: '600', marginTop: 2 }}>
                {col.sub}
              </Text>
            </View>
          ) : (
            <Text>{col.label}</Text>
          )}
          {sortColumn == col.id && sortDirection == 'desc'?
            <FontAwesome5 name={"sort-amount-up-alt"} style={{}} />
            : <FontAwesome5 name={"sort-amount-down-alt"} style={{}} />
          }
        </Pressable>
      ))}
      <Text style={[styles.columnHeader, styles.IconCell]}>{'QR'}</Text>
      <Text style={[styles.columnHeader, styles.IconCell]}>{'History'}</Text>
      <Text style={[styles.columnHeader, styles.IconCell]}>{'Action'}</Text>
      <Text style={[styles.columnHeader, styles.IconCell]}>{'Edit'}</Text>
      <Text style={[styles.columnHeader, styles.IconCell]}>{'DEL'}</Text>
    </View>
    <View style={{flex:1}}>
      <ScrollView>
        {renderTableData()}
      </ScrollView>
    </View>
  </View>), [filteredAndSortedData, checkedItemIds, visibleColumns])

  const addModalComponent = useMemo(()=>(
    <AddProductModal
      isModalVisible={isAddModalVisible}
      Product={selectedProduct}
      setUpdateProductsTrigger={setUpdateProductsTrigger}
      closeModal={closeAddProductModal}
      onViewExistingProduct={viewExistingProductFromAdd}
    />), [isAddModalVisible, selectedProduct])

  const quickAddModalComponent = useMemo(()=>(
    <QuickAddProductModal
      isModalVisible={isQuickAddModalVisible}
      setUpdateProductsTrigger={setUpdateProductsTrigger}
      closeModal={closeQuickAddProductModal}
      existingProducts={tableData}
    />), [isQuickAddModalVisible, tableData])
  
  const bulkLocationModalComponent = useMemo(()=>(
    <BulkUpdateLocationModal
      isModalVisible={isBulkUpdateLocationVisible}
      ids={checkedItemIds}
      setUpdateProductsTrigger={setUpdateProductsTrigger}
      closeModal={closeBulkUpdateLocation}
    />), [isBulkUpdateLocationVisible, checkedItemIds])

  const updateLocationModalComponent = useMemo(()=>(
    <UpdateLocationModal
      isModalVisible={isUpdateLocationVisible}
      selectedProducts={[selectedProduct]}
      setUpdateProductsTrigger={setUpdateProductsTrigger}
      closeModal={closeUpdateLocationModal}
      clearTableData={() => setCheckedItemIds([])}
    />), [isUpdateLocationVisible, selectedProduct])

  const bulkStatusModalComponent = useMemo(()=>(
    <BulkUpdateStatus
      isModalVisible={isBulkUpdateStatusVisible}
      ids={checkedItemIds}
      setUpdateProductsTrigger={setUpdateProductsTrigger}
      closeModal={closeBlukUPdateStatus}
    />), [isBulkUpdateStatusVisible, checkedItemIds])

  const qrModalComponent = useMemo(()=>(
    <ProductQRModal
      isModalVisible={isQRModalVisible}
      product={selectedQRProduct}
      closeModal={closeQRModal}
    />), [isQRModalVisible, selectedQRProduct])

  const columnPickerModalComponent = useMemo(()=>(
    <ColumnPickerModal
      isModalVisible={isColumnPickerVisible}
      columns={allColumns}
      selectedIds={visibleColumnIds || allColumns.map(c => c.id)}
      closeModal={() => setColumnPickerVisible(false)}
      onSaved={(ids) => setVisibleColumnIds(ids)}
    />), [isColumnPickerVisible, allColumns, visibleColumnIds])

  const importModalComponent = useMemo(()=>(
    <ImportProductsModal
      isModalVisible={isImportModalVisible}
      productFields={productFields}
      closeModal={() => setImportModalVisible(false)}
      onImported={() => {
        loadProductFields();
        getTable();
      }}
    />), [isImportModalVisible, productFields])

  return (
    <BasicLayout
      navigation={navigation}
      goBack={() => {
        openInventory(data?.searchOptions ? 'Product Lines' : null);
      }}
      screenName={'Products'}
      isLoading={isLoading}
    >
      <ScrollView horizontal={true}>
        <View style={styles.container}>
          <View style={styles.toolbar}>
            <View style={styles.searchBox}>
              <Text style={styles.searchLabel}>Category</Text>
              <Picker
                style={[styles.searchInput]}
                selectedValue={searchCategory}
                onValueChange={setSearchCategory}
              >
                <Picker.Item label={''} value={0} />
                {categories.length > 0 &&
                  categories.map((category, index) => {
                    return (
                      <Picker.Item key={index} label={category.category} value={category.id} />
                    );
                  })}
              </Picker>
            </View>
            <View style={styles.searchBox}>
              <Text style={styles.searchLabel}>Family</Text>
              <Picker
                style={[styles.searchInput]}
                selectedValue={searchFamily}
                onValueChange={setSearchFamily}
              >
                <Picker.Item label={''} value={0} />
                {families.length > 0 &&
                  families.map((family, index) => {
                    return <Picker.Item key={index} label={(family.family)} value={family.id} />;
                  })}
              </Picker>
            </View>
            <View style={styles.searchBox}>
              <Text style={styles.searchLabel}>Line</Text>
              <Picker
                style={[styles.searchInput]}
                selectedValue={searchProductLine}
                onValueChange={setSearchProductLine}
              >
                <Picker.Item label={''} value={0} />
                {lines.length > 0 &&
                  lines.map((line, index) => {
                    return (
                      <Picker.Item
                        key={index}
                        label={line.line + ' ' + line.size}
                        value={line.id}
                      />
                    );
                  })}
              </Picker>
            </View>
          </View>
          <View style={styles.toolbar}>
            {/* <View style={styles.searchBox}>
              <Text style={styles.searchLabel}>Product</Text>
              <TextInput
                style={styles.searchInput}
                placeholder=""
                value={searchProduct}
                onChangeText={setSearchProduct}
              />
            </View> */}
            <View style={styles.searchBox}>
              <Text style={styles.searchLabel}>Size</Text>
              <TextInput
                style={styles.searchInput}
                placeholder=""
                value={searchSize}
                onChangeText={setSearchSize}
              />
            </View>
            <View style={styles.searchBox}>
              <Text style={styles.searchLabel}>Barcode</Text>
              <TextInput
                style={styles.searchInput}
                placeholder=""
                value={searchBarcode}
                onChangeText={setSearchBarcode}
              />
            </View>
            <View style={styles.searchBox}>
              <Text style={styles.searchLabel}>Home Location</Text>
              <Picker
                style={[styles.searchInput]}
                selectedValue={searchHomeLocation}
                onValueChange={setSearchHomeLocation}
              >
                <Picker.Item label={''} value={0} />
                {Locations.length > 0 &&
                  Locations.map((location, index) => {
                    return (
                      <Picker.Item key={index} label={location.name || location.location} value={location.id} />
                    );
                  })}
              </Picker>
            </View>
            <View style={styles.searchBox}>
              <Text style={styles.searchLabel}>Current Location</Text>
              <Picker
                style={[styles.searchInput]}
                selectedValue={searchCurrentLocation}
                onValueChange={setSearchCurrentLocation}
              >
                <Picker.Item label={''} value={0} />
                {Locations.length > 0 &&
                  Locations.map((location, index) => {
                    return (
                      <Picker.Item key={index} label={location.name || location.location} value={location.id} />
                    );
                  })}
              </Picker>
            </View>
            <View style={styles.searchBox}>
              <Text style={styles.searchLabel}>Status</Text>
              <Picker
                style={[styles.searchInput]}
                selectedValue={searchStatus}
                onValueChange={setSearchStatus}
              >
                <Picker.Item label={'All'} value={''} />
                {Object.keys(StatusObj).map((value) => (
                  <Picker.Item key={value} label={StatusObj[value]} value={Number(value)} />
                ))}
              </Picker>
            </View>
            <View style={styles.searchBox}>
              <Text style={styles.searchLabel}>Last Scanned From</Text>
              {renderBOHTlbDatePicker(
                searchLastScannedStart,
                (date) => setSearchLastScannedStart(date ? formatDate(date) : ''),
                {},
                { isClearable: true, placeholderText: 'Start date' }
              )}
            </View>
            <View style={styles.searchBox}>
              <Text style={styles.searchLabel}>Last Scanned To</Text>
              {renderBOHTlbDatePicker(
                searchLastScannedEnd,
                (date) => setSearchLastScannedEnd(date ? formatDate(date) : ''),
                {},
                { isClearable: true, placeholderText: 'End date' }
              )}
            </View>
          </View>
          <View style={{ paddingHorizontal: 10, paddingVertical: 6, flexDirection: 'row', alignItems: 'center' }}>
            <Text style={{ fontWeight: '600', color: '#333' }}>
              {`Showing ${filteredAndSortedData.length} of ${tableData.length} item${tableData.length === 1 ? '' : 's'}`}
            </Text>
          </View>
          {buttonsElement}
          {tableElement}
        </View>
      </ScrollView>
      {addModalComponent}
      {quickAddModalComponent}
      {bulkLocationModalComponent}
      {updateLocationModalComponent}
      {bulkStatusModalComponent}
      {qrModalComponent}
      {columnPickerModalComponent}
      {importModalComponent}
    </BasicLayout>
  );
};

const styles = productsStyle;

export default Products;
